import { useState } from "react";
import { MessageCircle, Star, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import LeadFormWidget from "@/components/LeadFormWidget";
import ProjectForm from "@/components/ProjectForm";
import { trackEvent } from "@/utils/tracking";

const Hero = ({ settings, content }) => {
  const heroContent = content?.hero || {};
  const trustBar = content?.trust_bar || ["Kostenlose Beratung", "Schnelle Rückmeldung", "Persönlicher Ansprechpartner", "Regionale Umsetzung"];
  const reviewCount = content?.reviews?.count || 26;
  const reviewRating = content?.reviews?.rating || 5.0;
  const [activeTab, setActiveTab] = useState("quick");

  const openWhatsApp = () => {
    window.location.href = '/whatsapp';
  };

  return (
    <section className="relative pt-24 sm:pt-28 pb-12 sm:pb-16 bg-gradient-to-br from-[#faf8f5] via-white to-[#f5f0ea]" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">

          {/* Left Column */}
          <div className="pt-2">
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4A331D] leading-tight mb-4"
              style={{ fontFamily: "Work Sans, sans-serif" }}
              data-testid="hero-headline"
            >
              {heroContent.headline || "Haus renovieren, sanieren oder modernisieren lassen?"}
            </h1>

            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6" data-testid="hero-subheadline">
              {heroContent.subheadline || "BEKAJ BAU aus Großkrotzenburg ist Ihr Ansprechpartner für Innenausbau, Renovierungen, Sanierungen, Fassadenarbeiten und Bodenbeschichtungen im Raum Hanau, Aschaffenburg und Umgebung."}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="trust-bar">
              {trustBar.map((text, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700 font-medium">{text}</span>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-[#e8dfd4] p-4 shadow-sm mb-6" data-testid="trust-badge">
              <div className="flex items-center gap-3">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#FBBC04] text-[#FBBC04]" />
                  ))}
                </div>
                <div>
                  <span className="font-bold text-[#4A331D]">{reviewRating} Sterne</span>
                  <span className="text-gray-600 text-sm ml-1">bei {reviewCount} Google Bewertungen</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Echte Kundenbewertungen &bull; Vertrauenswürdig &bull; Regional
              </p>
            </div>

            <div className="hidden lg:block">
              <Button
                onClick={openWhatsApp}
                className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-6 py-5 text-base font-semibold rounded-lg shadow-md"
                data-testid="hero-whatsapp-button"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Jetzt per WhatsApp schreiben
              </Button>
            </div>
          </div>

          {/* Right Column - Tabbed Forms */}
          <div className="w-full" id="lead-form-hero">
            <div className="bg-white rounded-xl shadow-xl border border-[#e8dfd4] overflow-hidden" data-testid="hero-form-container">
              {/* Tabs */}
              <div className="flex border-b border-[#e8dfd4]" data-testid="form-tabs">
                <button
                  onClick={() => setActiveTab("quick")}
                  className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                    activeTab === "quick"
                      ? "text-[#4A331D] border-b-2 border-[#D4AA6A] bg-white"
                      : "text-gray-400 hover:text-gray-600 bg-gray-50"
                  }`}
                  data-testid="tab-quick"
                >
                  Schnellanfrage
                </button>
                <button
                  onClick={() => setActiveTab("project")}
                  className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                    activeTab === "project"
                      ? "text-[#4A331D] border-b-2 border-[#D4AA6A] bg-white"
                      : "text-gray-400 hover:text-gray-600 bg-gray-50"
                  }`}
                  data-testid="tab-project"
                >
                  Projekt beschreiben
                </button>
              </div>

              <div className="p-5 sm:p-6">
                {activeTab === "quick" ? (
                  <>
                    <div className="text-center mb-4">
                      <h2 className="text-lg sm:text-xl font-bold text-[#4A331D]">Kostenlose Einschätzung anfordern</h2>
                      <p className="text-gray-500 text-sm mt-1">Wir melden uns innerhalb von 24h</p>
                    </div>
                    <LeadFormWidget settings={settings} variant="hero" />
                  </>
                ) : (
                  <ProjectForm settings={settings} />
                )}

                {/* WhatsApp Fastlane */}
                <div className="mt-5 pt-5 border-t border-gray-200 text-center">
                  <p className="text-gray-500 text-xs mb-3">Oder direkt & unkompliziert</p>
                  <Button
                    onClick={openWhatsApp}
                    variant="outline"
                    className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white py-4 font-semibold rounded-lg"
                    data-testid="hero-whatsapp-fastlane"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Jetzt per WhatsApp schreiben
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
